<template>
    <div class="bulk-actions-overlay" @click.self="closeWizard">
        <div class="bulk-actions-container">
            <div v-if="!actionProcessing" class="close-button" @click="closeWizard">
                <md-icon>close</md-icon>
            </div>

            <simple-wizard
                :title="`Updating ${selectedRows.length} records`"
                :sub-title="getSubtitle"
                :is-multiple-transaction="false"
                :disable-navigation-headers="true"
                @finish="finishWizard"
                @stepChange="onStepChange"
            >
                <!-- CUSTOM FOOTER -->
                <template v-slot:footer="{ nextTab, prevTab, activeIndex, tabCount }">
                    <div v-if="activeIndex < tabCount - 1" class="wizard-footer-buttons">
                        <!-- Hide footer completely on final step -->
                        <div>
                            <md-button
                                v-if="activeIndex > 0"
                                class="btn-previous"
                                @click.native="customPrevTab(activeIndex, prevTab)"
                            >
                                Previous
                            </md-button>
                        </div>

                        <div>
                            <md-button class="btn-next" @click.native="customNextTab(activeIndex, nextTab)">
                                {{ activeIndex === 2 ? 'Confirm' : 'Next' }}
                            </md-button>
                        </div>
                    </div>
                    <div v-else></div>
                </template>

                <!-- Step 1: Choose Bulk Action -->
                <wizard-tab :label="'Step 1 - Choose Bulk Action'" :id="'chooseBulkActionTab'">
                    <transition name="slide-fade" mode="out-in">
                        <div class="wizard-tab-content">
                            <div class="wizard-tab-section">
                                <choose-bulk-action
                                    :selected-rows="selectedRows"
                                    :selected-action-index="selectedActionIndex"
                                    @action-selected="selectAction"
                                />
                            </div>
                        </div>
                    </transition>
                </wizard-tab>

                <!-- Step 2: Add Details -->
                <wizard-tab
                    :label="'Step 2 - ADD DETAILS'"
                    :before-change="validateStep2AddDetails"
                    :id="'addDetailsTab'"
                >
                    <transition name="slide-fade" mode="out-in">
                        <div class="wizard-tab-content">
                            <div class="wizard-tab-section">
                                <assign-driver
                                    v-if="selectedActionIndex === 0"
                                    :selected-driver-id="selectedDriverId"
                                    @driver-selected="selectDriver"
                                />
                                <change-status
                                    v-if="selectedActionIndex === 1"
                                    :selected-status="selectedStatus"
                                    @status-selected="selectStatus"
                                />
                                <add-stop-note
                                    v-if="selectedActionIndex === 2"
                                    :selected-note="selectedNote"
                                    :selected-note-type="selectedNoteType"
                                    @note-selected="selectNote"
                                    @note-type-selected="selectNoteType"
                                />
                                <set-date
                                    v-if="selectedActionIndex === 5"
                                    :selected-date="selectedDate"
                                    @date-selected="selectDate"
                                />
                            </div>
                        </div>
                    </transition>
                </wizard-tab>

                <!-- Step 3: Confirmation -->
                <wizard-tab :label="'Step 3 - Confirmation'" :id="'confirmationTab'">
                    <transition name="slide-fade" mode="out-in">
                        <div class="wizard-tab-content">
                            <div class="wizard-tab-section">
                                <confirm-bulk-action
                                    :selected-rows="selectedRows"
                                    :selected-driver-id="selectedDriverId"
                                    :selected-status="selectedStatus"
                                    :selected-note="selectedNote"
                                    :selected-note-type="selectedNoteType"
                                    :selected-date="selectedDate"
                                    :selected-action-index="selectedActionIndex"
                                    :team-members="teamMembers"
                                />
                            </div>
                        </div>
                    </transition>
                </wizard-tab>

                <!-- Step 4: Progress -->
                <wizard-tab :label="'Step 4 - Processing'" :before-change="() => false" :id="'processingTab'">
                    <transition name="slide-fade" mode="out-in">
                        <div class="wizard-tab-content">
                            <div class="wizard-tab-section">
                                <bulk-action-progress
                                    :action-completed="actionCompleted"
                                    @acknowledge="acknowledgeComplete"
                                />
                            </div>
                        </div>
                    </transition>
                </wizard-tab>
            </simple-wizard>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import { handleRequests, showErrorMessage } from '@/helpers';
import { SimpleWizard, WizardTab } from '@/components';
import { HUB_NAMES, HUB_ACTIONS, DATE_TYPES, SIGNALR_CONNECTION_STATE } from '@/utils/constants';
import {
    ChooseBulkAction,
    AssignDriver,
    ChangeStatus,
    AddStopNote,
    ConfirmBulkAction,
    BulkActionProgress,
    SetDate
} from './wizard';

export default {
    name: 'BulkActionsWizard',
    components: {
        SimpleWizard,
        WizardTab,
        ChooseBulkAction,
        AssignDriver,
        ChangeStatus,
        AddStopNote,
        ConfirmBulkAction,
        BulkActionProgress,
        SetDate
    },
    props: {
        selectedRows: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            selectedActionIndex: 0, // Default to first action (Assign to Driver)
            selectedDriverId: '',
            selectedStatus: '',
            selectedNote: '',
            selectedNoteType: 'Stop Note',
            selectedDate: null,
            teamMembers: [],
            actionCompleted: false,
            actionProcessing: false,
            currentStep: '0',
            signalRHandlers: [],
            signalRTimeout: null
        };
    },
    computed: {
        getSubtitle() {
            return 'Perform bulk actions on selected records';
        },
        ...mapGetters({
            getConnectionStatus: 'signalR/getConnectionStatus',
            getConnection: 'signalR/getConnection'
        })
    },
    async mounted() {
        // Load team members
        await this.fetchTeamMembers();

        // Set up SignalR handlers
        this.signalRHandlers.push({
            action: HUB_ACTIONS.BulkActionComplete,
            handler: async () => {
                this.actionCompleted = true;
            }
        });

        // Setup SignalR connection
        await this.setupSignalR();
    },
    beforeDestroy() {
        clearTimeout(this.signalRTimeout);
        this.removeSignalRHandlers();
    },
    methods: {
        async fetchTeamMembers() {
            try {
                const teamMembers = await this.$store.dispatch('team/FETCH_TEAM_MEMBERS');
                this.teamMembers = teamMembers;
            } catch (error) {
                showErrorMessage(this, 'Failed to load team members', error);
            }
        },
        selectAction(index) {
            this.selectedActionIndex = index;
        },
        selectDriver(driverId) {
            this.selectedDriverId = driverId;
        },
        selectStatus(status) {
            this.selectedStatus = status;
        },
        selectNote(note) {
            this.selectedNote = note;
        },
        selectNoteType(noteType) {
            this.selectedNoteType = noteType;
        },
        selectDate(date) {
            this.selectedDate = date;
        },
        validateStep2AddDetails() {
            // Driver selection or status selection is required based on the action
            if (this.selectedActionIndex === 0) {
                return !!this.selectedDriverId;
            }
            if (this.selectedActionIndex === 1) {
                return !!this.selectedStatus;
            }
            if (this.selectedActionIndex === 2) {
                return !!this.selectedNote;
            }
            if (this.selectedActionIndex === 5) {
                return true; // Date can be null to clear dates
            }

            // Delete and Unassign actions don't need additional details.
            return true;
        },
        onStepChange(stepId) {
            this.currentStep = stepId;

            // If we're on the final step, start the bulk action
            if (stepId === 'processingTab') {
                this.actionProcessing = true;
                this.executeBulkAction();
            }
        },
        closeWizard() {
            // Ensure the close event is emitted to the parent component
            if (!this.actionProcessing) {
                this.$emit('close');
            }
        },
        async executeBulkAction() {
            try {
                let api;
                let payload;

                if (this.selectedActionIndex === 0) {
                    // Assign to Driver
                    api = '/api/jobs/bulk/assign-user';
                    payload = {
                        method: 'post',
                        data: {
                            stopOrShipmentIds: this.selectedRows,
                            assignToPublicUserId: this.selectedDriverId
                        }
                    };
                } else if (this.selectedActionIndex === 1) {
                    // Change Status
                    api = '/api/jobs/bulk/change-status';
                    payload = {
                        method: 'post',
                        data: {
                            stopOrShipmentIds: this.selectedRows,
                            newStatus: this.selectedStatus
                        }
                    };
                } else if (this.selectedActionIndex === 2) {
                    // Add Stop Note
                    api = '/api/jobs/bulk/add-note';
                    payload = {
                        method: 'post',
                        data: {
                            stopOrShipmentIds: this.selectedRows,
                            note: this.selectedNote,
                            noteType: this.selectedNoteType
                        }
                    };
                } else if (this.selectedActionIndex === 3) {
                    // Delete
                    api = '/api/jobs/bulk/delete';
                    payload = {
                        method: 'post',
                        data: {
                            stopOrShipmentIds: this.selectedRows
                        }
                    };
                } else if (this.selectedActionIndex === 4) {
                    // Unassign
                    api = '/api/jobs/bulk/assign-user';
                    payload = {
                        method: 'post',
                        data: {
                            stopOrShipmentIds: this.selectedRows,
                            assignToPublicUserId: '' // Empty string for unassigning
                        }
                    };
                } else if (this.selectedActionIndex === 5) {
                    const newDate = this.selectedDate
                        ? moment(this.selectedDate).format(DATE_TYPES.internationalDate)
                        : null;

                    api = '/api/jobs/bulk/change-date';
                    payload = {
                        method: 'post',
                        data: {
                            stopOrShipmentIds: this.selectedRows,
                            date: newDate
                        }
                    };
                }

                await handleRequests(api, payload);

                this.actionProcessing = true;

                // We'll wait for the SignalR event to complete the action
            } catch (error) {
                this.actionProcessing = true;
                showErrorMessage(this, 'Failed to execute bulk action', error);
                this.$emit('close');
            }
        },
        acknowledgeComplete() {
            this.$emit('close');
            this.$emit('refresh');
        },
        customPrevTab(activeIndex, prevTab) {
            // For Delete action (index 3) or Unassign action (index 4), if we're on step 3 (confirmation), go back to step 1 (choose action)
            if ((this.selectedActionIndex === 3 || this.selectedActionIndex === 4) && activeIndex === 2) {
                // We need to go back to step 1 (index 0) since step 2 is skipped
                // Use the wizard's prevTab twice to go back to step 1
                prevTab();
                this.$nextTick(() => {
                    prevTab();
                });
            } else {
                // Normal back button behavior
                prevTab();
            }
        },
        customNextTab(activeIndex, nextTab) {
            // For Delete action (index 3) or Unassign action (index 4), if we're on step 1 (choose action), go forward to step 3 (confirmation)
            if ((this.selectedActionIndex === 3 || this.selectedActionIndex === 4) && activeIndex === 0) {
                // Skip step 2 for delete and unassign actions by calling nextTab twice
                nextTab();
                this.$nextTick(() => {
                    nextTab();
                });
            } else {
                // Normal next button behavior
                nextTab();
            }
        },
        finishWizard() {
            // This should never be called since we control the flow manually
            this.$emit('close');
        },
        async setupSignalR() {
            try {
                await this.$store.dispatch('signalR/connect', HUB_NAMES.JobHub);
                const connection = this.getConnection(HUB_NAMES.JobHub);

                // Verify connection is established before proceeding
                if (!connection || connection.state !== SIGNALR_CONNECTION_STATE.Connected) {
                    throw new Error('SignalR connection not established');
                }

                await connection.invoke(HUB_ACTIONS.JoinToTeamChannel);
                await this.$store.dispatch('signalR/registerHandlers', {
                    hubName: HUB_NAMES.JobHub,
                    handlers: this.signalRHandlers
                });
            } catch (e) {
                console.error('Error connecting to job hub:', e);
            }
        },
        removeSignalRHandlers() {
            try {
                const connection = this.getConnection(HUB_NAMES.JobHub);

                if (connection && connection.state === SIGNALR_CONNECTION_STATE.Connected) {
                    this.$store.dispatch('signalR/unregisterHandlers', {
                        hubName: HUB_NAMES.JobHub,
                        handlers: this.signalRHandlers
                    });
                    connection.invoke(HUB_ACTIONS.RemoveFromTeamChannel);
                }
            } catch (error) {
                console.error('Error removing SignalR handlers:', error);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.bulk-actions-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.bulk-actions-container {
    position: relative;
    width: 80%;
    max-width: 1000px;
    margin: 0 auto;
}

.close-button {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
    cursor: pointer;
    background-color: rgba(255, 255, 255, 0.8);
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);

    &:hover {
        background-color: rgba(255, 255, 255, 1);
    }

    .md-icon {
        color: #333;
    }
}

/* Slide transition animations */
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.3s ease;
}

.slide-fade-enter {
    transform: translateX(30px);
    opacity: 0;
}

.slide-fade-leave-to {
    transform: translateX(-30px);
    opacity: 0;
}

/* Override modal styles to constrain width */
::v-deep .modal-wrapper {
    max-width: 80%;
    margin: 0 auto;
}

.wizard-tab-content {
    padding: 20px;
}

.wizard-tab-section {
    margin-bottom: 20px;
}

.wizard-footer-buttons {
    display: flex;
    justify-content: space-between;
    width: 100%;
}
</style>
